import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addDays,
  endOfDay,
  isWithinInterval,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
} from "date-fns/fp";
import {
  allPass,
  converge,
  filter,
  identity,
  map,
  pipe,
  prop,
  reduce,
  subtract,
  times,
} from "ramda";
import { useCallback, useContext, useMemo, useState } from "react";

import { isToday } from "date-fns";
import { defineMessages } from "react-intl";
import { anchorTime } from "../../components/Forms";
import { Icon } from "../../components/Icon";
import { LayoutCtx } from "../../components/Layout";
import { useMsg } from "../../components/Msg/Msg";
// import { useAuth } from "../Authorization";
import * as tz from "date-fns-tz";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import { useSessionIsNotCancellable } from "../../components/ScheduledSessions/CancelSessionButton";
import { isDevelopment } from "../../utils/environment";
import { coachesMessages } from "../Coaches/messages";
import { I18nContext } from "../I18n/I18nProvider";
import { ConfirmModal } from "../Modal/ConfirmModal";
import { QueryRenderer } from "../QM/QueryRenderer";
import {
  getIsDayLoading,
  isIntervalWithin,
  useAvailabilityQueries,
  usePickSlotMutation,
} from "./api";
import { getCalendarInterval } from "./getCalendarInterval";

const HEADER_FORMAT = "d MMM";

// const computeSlotStats = pipe( reduce( ({ lowestHour, highestHour }, interval) => { const startHour = getHours(interval.start); const endHour = getHours(fixEnd(interval.end)); if (interval.end - interval.start > 60 * 60 * 1000) console.log("[computeSlotStats] interval longer than 1 hour", { interval, }); return { lowestHour: Math.min(lowestHour, startHour), highestHour: Math.max(highestHour, endHour), }; }, { lowestHour: 12, highestHour: 12 } ), ({ lowestHour, highestHour }) => ({ slotsCount: Math.max(highestHour - lowestHour + 1, 3), firstHour: lowestHour || 9, lowestHour, highestHour, }) );

const getAnchoredTimeRange = (dayDate = new Date()) =>
  pipe(
    reduce(
      (acc, { start, end }) => ({
        start: Math.min(acc.start, +anchorTime(dayDate, start)),
        end: Math.max(acc.end, +anchorTime(dayDate, end)),
      }),
      { start: +endOfDay(dayDate), end: +startOfDay(dayDate) },
    ),
    map((timestamp) => new Date(timestamp)),
  );

const minsToHeight = (mins) => mins * 0.6;
const intervalToHeight = converge(
  pipe(subtract, (duration) => duration / (1000 * 60), minsToHeight),
  [prop("end"), prop("start")],
);

export const CREATE_OFFSET =
  (date, map = identity) =>
  (daysOffset, hour) =>
    pipe(
      startOfDay,
      addDays(daysOffset),
      setHours(hour),
      setMinutes(0),
      setSeconds(0),
      map,
    )(date);

const FORMAT = {
  FULL: `Pp (O)`,
  LONG_FULL: `"PPPPp (O)"`, // pppp has incorrect timezone
  TIME: `HH:mm`,
};

// TODO: split in two components:
// - AvailabilityCalendar - smart container
// - AvailabilityCalendarView - dumb view (testable)
export const AvailabilityCalendar = ({
  coach,
  onContact,
  onPick,
  pickPending,
  visibleDaysCount: visibleDaysCountProp,
  sx,
  dateNow: dateNowProp,
  disablePickSlot = false,
  cellWidth = "47px",
  fetchDisabled = false,
  calendarTimeZone: calendarTimeZoneProp,
  messages = {},
}) => {
  const { i18n, userTz } = useContext(I18nContext);
  const calendarTimeZone = calendarTimeZoneProp || userTz;
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezonesMismatch = userTz !== browserTz;

  const theme = useTheme();
  const onlyLg = useMediaQuery(theme.breakpoints.only("lg"));
  const visibleDaysCount = visibleDaysCountProp || onlyLg ? 3 : 7;
  const msg = useMsg({ dict: messages });
  const coachesMsg = useMsg({ dict: coachesMessages });
  const dateNow = useMemo(() => dateNowProp || new Date(), [dateNowProp]);

  const [offsetDays, setOffsetDays] = useState(0);
  const calendarInterval = useMemo(
    // TODO: should be in userTz?
    () => getCalendarInterval({ dateNow, offsetDays, visibleDaysCount }),
    [offsetDays, dateNow, visibleDaysCount],
  );

  const createAddOffset = (days) => () => setOffsetDays((d) => d + days);
  const [pickSlot, setPickSlot] = useState();

  const { someResultsQuery, queries } = useAvailabilityQueries({
    username: coach?.username,
    calendarInterval,
    disabled: fetchDisabled,
  });
  const pickSlotMutation = usePickSlotMutation({
    username: coach?.username,
    onSuccess: () => {
      setPickSlot();
    },
  });

  // const { user } = useAuth();
  // const pickedCoach = user.data.coach;

  const onTimeslotClick = useCallback(
    (slot) => {
      if (disablePickSlot) return;
      setPickSlot({
        interval: slot,
        originalApiSlot: slot.originalApiSlot,
        coach,
      });
    },
    [disablePickSlot, coach],
  );
  const { reset } = pickSlotMutation;
  const handleClosePickSlotModal = useCallback(() => {
    setPickSlot();
    reset();
  }, [reset]);
  const isNotCancellable =
    useSessionIsNotCancellable({
      date:
        pickSlot?.interval?.start ||
        new Date(10 * 24 * 60 * 60 * 1000 + Date.now()),
    }) || true;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        ...sx,
      }}
    >
      <Box
        bgcolor="#DAD2F1"
        height={44}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderRadius={"6px"}
        color="primary.main"
        fontWeight={500}
      >
        <IconButton
          variant="outlined"
          size="small"
          sx={{ color: "inherit" }}
          disabled={offsetDays < 1}
          onClick={createAddOffset(-1)}
        >
          <Icon name={"ChevronLeft"} />
        </IconButton>
        <IconButton
          variant="outlined"
          size="small"
          sx={{ color: "inherit" }}
          disabled={offsetDays < visibleDaysCount}
          onClick={createAddOffset(-visibleDaysCount)}
        >
          <Icon name={"KeyboardDoubleArrowLeft"} />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 2,
          }}
        >
          {i18n.formatLocal(calendarInterval.start, HEADER_FORMAT)} -{" "}
          {i18n.formatLocal(calendarInterval.end, HEADER_FORMAT)}
        </Box>
        <IconButton
          variant="outlined"
          size="small"
          sx={{ color: "inherit" }}
          onClick={createAddOffset(visibleDaysCount)}
        >
          <Icon name={"KeyboardDoubleArrowRight"} />
        </IconButton>
        <IconButton
          variant="outlined"
          size="small"
          sx={{ color: "inherit" }}
          onClick={createAddOffset(1)}
        >
          <Icon name={"ChevronRight"} />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderBottom: "1px solid #EAECF0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 1,
            // px: 1,
          }}
        >
          {times(identity, visibleDaysCount).map((index) => {
            const date = addDays(index, calendarInterval.start);

            return (
              <Box
                sx={{
                  p: 0.5,
                  pb: 0.5,
                  mb: 0.5,
                  position: "relative",
                  textAlign: "center",
                  width: cellWidth,
                  border: isToday(date) ? "1px solid #DAD2F1" : "none",
                  borderRadius: 1,
                }}
              >
                {i18n.formatLocal(date, "E")}
                <br />
                {i18n.formatLocal(date, "d")}
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{
            height: 125,
            overflow: "auto",
          }}
        >
          <QueryRenderer
            query={someResultsQuery}
            loaderName="Block"
            success={({ data: utcSlotIntervals }) => {
              if (!Array.isArray(utcSlotIntervals)) return null;

              const zonedSlotIntervals = utcSlotIntervals.map((slot) => ({
                ...slot,
                start: utcToZonedTime(slot.start, calendarTimeZone),
                end: utcToZonedTime(slot.end, calendarTimeZone),
              }));
              const anchoredInterval =
                getAnchoredTimeRange(dateNow)(zonedSlotIntervals);

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    gap: 1,
                    // px: 1,
                  }}
                >
                  {times(identity, visibleDaysCount).map((index) => {
                    const date = addDays(index, calendarInterval.start);
                    const dayInterval = {
                      start: startOfDay(date),
                      end: endOfDay(date),
                    };

                    const zonedDaySlots = filter(
                      allPass([
                        (interval) => interval.start > dateNow,
                        isIntervalWithin({
                          parentInterval: dayInterval,
                          overlapping: "debugger",
                        }),
                      ]),
                      zonedSlotIntervals,
                    );
                    const isPending = getIsDayLoading({
                      queries,
                      dayInterval,
                      userTz,
                    });
                    const visibleDayInterval = {
                      start: anchorTime(date, anchoredInterval.start),
                      end: anchorTime(date, anchoredInterval.end),
                    };

                    const displayedMs =
                      visibleDayInterval.end - visibleDayInterval.start;
                    // assuming one slot === 1 hour
                    const slotsCount =
                      Math.max(
                        0, // loading - negative
                        Math.ceil(displayedMs / (1000 * 60 * 60)),
                      ) || 0; // NaN
                    const unavailableSlots = Array(slotsCount)
                      .fill(null)
                      .map((_, idx) => {
                        const slotStart = new Date(
                          +visibleDayInterval.start + idx * 60 * 60 * 1000,
                        );
                        const isAvailable = zonedDaySlots.some((interval) =>
                          isWithinInterval(interval, slotStart),
                        );
                        if (isAvailable) return null;
                        return {
                          idx,
                          top: intervalToHeight({
                            start: visibleDayInterval.start,
                            end: slotStart,
                          }),
                          height: minsToHeight(60) - 5,
                          slotStart,
                          // _: { visibleDayInterval, displayedMs, slotsCount, displayedHours: displayedMs / (1000 * 60 * 60), },
                        };
                      })
                      .filter(Boolean);

                    // prettier-ignore-next-line
                    // console.log( "[AvailabilityCalendar.rndr] DAY rndr prop", date, {displayedMs, slotsCount, unavailableSlots}, { date, dayInterval, zonedDaySlots }, { anchoredInterval, zonedSlotIntervals, visibleDaysCount } );

                    return (
                      <Box
                        key={date.toISOString()}
                        sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          textAlign: "center",
                          gap: 1,
                          width: cellWidth,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            height: intervalToHeight(visibleDayInterval),
                          }}
                        >
                          {unavailableSlots.map(
                            ({ idx, top, height, slotStart }) => {
                              const text = i18n.formatLocalMaybe(
                                slotStart,
                                "p",
                              );
                              return (
                                <Box
                                  key={text + idx}
                                  sx={{
                                    position: "absolute",
                                    height,
                                    lineHeight: `${height}px`,
                                    top,
                                    cursor: "default",
                                    borderRadius: "6px",
                                    bgcolor: "transparent",
                                    color: "inherit",
                                    fontWeight: 400,
                                    width: "100%",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {isPending ? (
                                    <Skeleton />
                                  ) : (
                                    <span title={text}> - </span>
                                  )}
                                </Box>
                              );
                            },
                          )}
                          {zonedDaySlots.map((interval) => {
                            // const inUserTz = tz.utcToZonedTime(interval.start, userTz);
                            const onClick =
                              interval &&
                              onTimeslotClick &&
                              !isPending &&
                              (() => onTimeslotClick(interval));
                            const height = intervalToHeight(interval) - 5;

                            const utcDate = tz.zonedTimeToUtc(
                              interval.start,
                              calendarTimeZone,
                            );
                            const profileTzFormatted = tz.format(
                              interval.start,
                              FORMAT.FULL,
                              {
                                timeZone: userTz,
                              },
                            );
                            const profileTzShortFormatted = tz.format(
                              interval.start,
                              FORMAT.TIME,
                              { timeZone: userTz },
                            );

                            const browserTzFormatted = tz.format(
                              utcDate,
                              FORMAT.FULL,
                              {
                                timeZone: browserTz, // not needed? Seems it works the same without this
                              },
                            );
                            const utcFormatted = tz.format(
                              tz.utcToZonedTime(utcDate, "UTC"),
                              FORMAT.FULL,
                              {
                                timeZone: "UTC",
                              },
                            );

                            const render = (children) =>
                              !timezonesMismatch ? (
                                children
                              ) : (
                                <Tooltip
                                  followCursor
                                  title={
                                    <>
                                      {!isDevelopment() ? null : (
                                        <>
                                          originalApiSlot:{" "}
                                          {interval.originalApiSlot} {userTz}
                                          <br />
                                        </>
                                      )}
                                      {profileTzFormatted} ({userTz})
                                      <br />
                                      {browserTzFormatted} ({browserTz})
                                      <br />
                                      {utcFormatted} (UTC)
                                    </>
                                  }
                                >
                                  {children}
                                </Tooltip>
                              );

                            return render(
                              <Box
                                key={+interval.start}
                                sx={{
                                  position: "absolute",
                                  height: height,
                                  lineHeight: `${height}px`,
                                  top: intervalToHeight({
                                    start: visibleDayInterval.start,
                                    end: interval.start,
                                  }),
                                  cursor: onClick ? "pointer" : "default",
                                  borderRadius: "6px",
                                  // py: 0.9,
                                  // px: 0.5,
                                  bgcolor: "#F9F8FF",
                                  color: "primary.main",
                                  fontWeight: 500,
                                  width: "100%",
                                  overflowX: "hidden",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  // textOverflow: "ellipsis",
                                }}
                                onClick={onClick}
                              >
                                {isPending ? (
                                  <Skeleton />
                                ) : (
                                  <span>{profileTzShortFormatted}</span>
                                )}
                              </Box>,
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          />
        </Box>
      </Box>
      <ConfirmModal
        open={!!pickSlot}
        onClose={handleClosePickSlotModal}
        iconName="RocketLaunch"
        title={msg("availability-calendar.confirm-reservation.title", {
          date: !pickSlot?.interval?.start
            ? ""
            : tz.format(pickSlot?.interval?.start, FORMAT.LONG_FULL, {
                timeZone: userTz,
                locale: i18n.locale,
              }) + (timezonesMismatch ? ` (${userTz})` : ""),
          // date: i18n.formatLocalMaybe(pickSlot?.interval?.start, "PPPPpppp"),
        })}
        desc={{
          children: (
            <>
              {msg("availability-calendar.confirm-reservation.desc", {
                name: `${coach?.firstName} ${coach.lastName}`,
              })}
              <br />
              <br />
              {coachesMsg("your-coach.session-link-info")}
              <br />
              <br />
              <Box sx={{ fontWeight: isNotCancellable ? 600 : 400 }}>
                {msg(
                  "availability-calendar.confirm-reservation.cancellation-restriction",
                )}
              </Box>
            </>
          ),
        }}
        buttons={[
          {
            variant: "outlined",
            type: "button",
            children: msg("availability-calendar.cancel.btn"),
            onClick: handleClosePickSlotModal,
          },
          {
            component: Button,
            variant: "contained",
            type: "button",
            children: msg("availability-calendar.confirm.btn"),
            disabled: pickSlotMutation.isPending,
            loading: pickSlotMutation.isPending,
            onClick: () => pickSlotMutation.mutate(pickSlot),
          },
        ]}
        sx={{ width: "800px" }}
      >
        <QueryRenderer
          query={pickSlotMutation}
          success={() => null}
          loading={() => null}
          errored={({ error }) => (
            <Alert severity="error">{error?.message || "Oops!"}</Alert>
          )}
        />
      </ConfirmModal>
    </Box>
  );
};

export const messages = defineMessages({
  "availability-calendar.confirm-reservation.title": {
    id: "availability-calendar.confirm-reservation.title",
    defaultMessage: "Confirm reservation {date}",
  },
  "availability-calendar.confirm-reservation.desc": {
    id: "availability-calendar.confirm-reservation.desc",
    defaultMessage:
      "By booking this time slot, you are confirming that you want to proceed with coaching sessions with {name}.", // TODO: change textation in input.csv
  },
  "availability-calendar.confirm-reservation.cancellation-restriction": {
    id: "availability-calendar.confirm-reservation.cancellation-restriction",
    defaultMessage:
      "To respect the commitment and time allocation of coaches, rescheduling or canceling the session is only possible at least 24 hours before the session start time.",
  },
  "coaches.coach.pick": {
    id: "coaches.coach.pick",
    defaultMessage: "Pick the Coach",
  },
  "coaches.coach.change-picked": {
    id: "coaches.coach.change-picked",
    defaultMessage: "Change coach",
  },
  "availability-calendar.cancel.btn": {
    id: "availability-calendar.cancel.btn",
    defaultMessage: "Cancel",
  },
  "availability-calendar.confirm.btn": {
    id: "availability-calendar.confirm.btn",
    defaultMessage: "Confirm",
  },
  "availability-calendar.hide.btn": {
    id: "availability-calendar.hide.btn",
    defaultMessage: "Hide calendar",
  },
  "availability-calendar.check.btn": {
    id: "availability-calendar.check.btn",
    defaultMessage: "Check avaliability",
  },
});
